import { Stack } from "@mui/material";
import { type GridColDef, type GridSortModel } from "@mui/x-data-grid-pro";
import { useCallback, useMemo } from "react";

import {
  type DataGridHeaderSortDirection,
  type IDataGridHeaderItem,
} from "~/components/UI/DataGridHeader";
import {
  labelByMarketsDataGridField,
  tooltipByMarketsDataGridField,
} from "~/feature/event/components/MarketsDataGrid/constants";
import {
  marketColumnSizing,
  marketPinnedColumnsPadding,
} from "~/feature/event/components/MarketsDataGrid/marketColumnWidth";
import { MarketsDataGridField } from "~/feature/event/components/MarketsDataGrid/types";
import { SwitchGrouping } from "~/feature/event/components/SwitchGrouping";

interface IUseMarketsDataGridHeaderParams {
  columns: GridColDef[];
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  isGrouped: boolean;
  isMobile: boolean;
  isMarketPinned: boolean;
}

export const useMarketsDataGridHeader = ({
  columns,
  sortModel,
  onSortModelChange,
  isGrouped,
  isMobile,
  isMarketPinned,
}: IUseMarketsDataGridHeaderParams) => {
  const currentSort = useMemo(() => sortModel[0], [sortModel]);

  const items = useMemo<IDataGridHeaderItem[]>(
    () =>
      Object.values(MarketsDataGridField).map((field) => {
        const column = columns.find((item) => item.field === field);

        return {
          field,
          label: labelByMarketsDataGridField[field],
          tooltip: tooltipByMarketsDataGridField[field],
          pinned: isMarketPinned && field === MarketsDataGridField.Market,
          pinnedLeftPadding: marketPinnedColumnsPadding[field],
          ...marketColumnSizing[field],
          sortable: column?.sortable !== false,
          children:
            field === MarketsDataGridField.Market && isGrouped && isMobile ? (
              <Stack
                sx={{
                  width: "100%",
                  flexDirection: "row",
                  gap: 0.6
                }}>
                <SwitchGrouping />
              </Stack>
            ) : undefined,
          sortDirection:
            currentSort?.field === field ? (currentSort?.sort ?? null) : null,
        };
      }),
    [
      columns,
      currentSort?.field,
      currentSort?.sort,
      isGrouped,
      isMobile,
      isMarketPinned,
    ]
  );

  const handleSortChange = useCallback(
    (field: string, direction: DataGridHeaderSortDirection) => {
      onSortModelChange([
        { field: field as MarketsDataGridField, sort: direction },
      ]);
    },
    [onSortModelChange]
  );

  return {
    items,
    handleSortChange,
  };
};
